import React, {useEffect, useMemo, useState} from 'react';
import {postData} from "@/utils/sendData";
import styles from './Tariffs.module.scss';
import {API} from "@/api/API";
import CheckIcon from '@/assets/images/icons/24/check.svg';
import ArrowIcon from '@/assets/images/icons/16/tooltipArrow.svg';
import {Period, Service, Tariff, TariffsInterface} from "@/views/Promo/Prices/Tariffs/Tariffs.interface";
import {TariffsSchema} from "@/views/Promo/Prices/Tariffs/Tariffs.schema";

const Tariffs = () => {
    const [data, setData] = useState<TariffsInterface>({...TariffsSchema});
    const [activePeriod, setActivePeriod] = useState<number>(0);
    const isAcademy = window.location.host.includes('academy') ? 'academy' : null;

    useEffect(() => {
        handleGetTariffs()
    }, []);

    const handleGetTariffs = async () => {
        try {
            const res = await postData(null, API.getTariffs.path(), API.getTariffs.method);
            setData(res.data.data)
        } catch (err) {
            console.log(err)
        }
    }

    const getLimitValue = (tariff: Tariff, serviceId: number) => {
        const limit = tariff.limits.find(limit => limit.service_id === serviceId);
        if (!limit) return '—';

        if (!limit.limit_text && !limit.comment) {
            return (
                <CheckIcon/>
            )
        }
        if (limit.comment) {
            return limit.comment
        }
        if (typeof limit.limit_text === 'object') {
            return `${limit.limit_text.value} ${limit.limit_text.add}`;
        }

        return (`${limit.limit_text.toLocaleString('ru-RU')} ${limit.limit_period_medium}`);
    };

    const renderPrices = (tariff: Tariff) => {
        return (
            <div className={styles.tooltip}>
                <div className={styles.tooltipToggle}>
                    {tariff.periods[activePeriod] ? `${tariff.periods[activePeriod]?.price_text.split(' руб.')[0]} ₽/` : '—'}{tariff.periods[activePeriod]?.period_text_short}
                </div>
                <div className={styles.tooltipContainer}>
                    <div className={styles.tooltipBody}>
                        {tariff.periods.map((period: Period, index: number) =>
                            <div className={styles.tooltipText} key={index}>
                                <p>{period.period_text} {data?.discounts[period?.period] ?
                                    <span className={styles.discount}>
                                        -{data?.discounts[period?.period]}%
                                    </span> : null}
                                </p>
                                <p className={styles.tooltipPrice}>
                                    {period.price_text.split(' руб.')[0]}<span>  ₽/{period.period_text_short}</span>
                                </p>
                            </div>
                        )}
                    </div>
                    <ArrowIcon/>
                </div>
            </div>
        )
    }

    const uniquePeriods = useMemo(() => {
        const periodMap = new Map();

        data?.tariffs.forEach((tariff: Tariff) => {
            tariff.periods?.forEach((period: Period) => {
                if (!periodMap.has(period.period)) {
                    periodMap.set(period.period, period);
                }
            });
        });

        return Array.from(periodMap.values());
    }, [data?.tariffs]);

    return (
        <div className={styles.tableContainer}>
            <h6 className={styles.title}>Тарифы услуг</h6>
            <p className={styles.subtitle}>Базовые уведомления по {isAcademy ? "курсам" : "событиям"} от
                имени {isAcademy ? "Nethouse.Академия" : "Nethouse.События"} и
                почты {isAcademy ? 'academy@nethouse.ru' : 'events@nethouse.ru'} отправляются бесплатно.</p>
            {uniquePeriods.length > 1 ?
                <div className={styles.periods}>
                    {uniquePeriods.map((period: Period, index: number) =>
                        <div
                            key={index}
                            className={[styles.period, index === activePeriod ? styles.active : ''].filter(x => x ?? x).join(' ')}
                            onClick={() => setActivePeriod(index)}
                        >
                            {period.period_text}
                            {data?.discounts[period?.period] ?
                                <label className={styles.discount}>
                                    -{data?.discounts[period?.period]}%
                                </label> : null
                            }
                        </div>
                    )}
                </div> : null
            }
            <table className={styles.table}>
                <thead>
                <tr>
                    <th></th>
                    {data?.tariffs.map((tariff: Tariff) =>
                        <th key={tariff.id}>
                            <div className={styles.tariffNames}>
                                <p className={styles.name}>{tariff.name}</p>
                                <p className={styles.description}>{tariff.description}</p>
                            </div>
                        </th>
                    )}
                </tr>
                </thead>
                <tbody>
                <tr className={styles.color}>
                    <td></td>
                    {data?.tariffs.map((tariff: Tariff) => (
                        <td key={`price-${tariff.id}`} className={styles.prices}>
                            {renderPrices(tariff)}
                        </td>
                    ))}
                </tr>
                {data?.services.map((service: Service, index: number) => (
                        <tr key={service.id}
                            className={[styles.services, index % 2 === 0 ? '' : styles.color].filter(x => x ?? x).join(' ')}>
                            <td className={styles.service}>{service.name}</td>
                            {data?.tariffs.map((tariff) => (
                                <td key={tariff.id} className={styles.limit}>
                                    {getLimitValue(tariff, service.id)}
                                </td>
                            ))}
                        </tr>
                    )
                )}
                </tbody>
            </table>
            <div className={styles.mobile}>
                {data?.tariffs.map((tariff: Tariff, index: number) => (
                    <div className={styles.mobileCard} key={`mobile-index-${index}`}>
                        <div className={styles.tariffNames}>
                            <p className={styles.name}>{tariff.name}</p>
                            <p className={styles.description}>{tariff.description}</p>
                        </div>
                        <div className={[styles.pricesWrapper, styles.color].filter(x => x ?? x).join(' ')}>
                            <p>Цена</p>
                            <div className={styles.prices}>
                                {renderPrices(tariff)}
                            </div>
                        </div>
                        {data?.services.map((service: Service, index: number) =>
                            <div key={`mobile-${index}`}
                                 className={[styles.service, index % 2 === 0 ? '' : styles.color].filter(x => x ?? x).join(' ')}>
                                {service.name}
                                <div className={styles.limit}>
                                    {getLimitValue(tariff, service.id)}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Tariffs;
